import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Save, Key, Bell, Building2, Link, Settings, Shield, Mail, Phone, MapPin, Globe, CheckCircle2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export default function ClientSettings() {
  const handleSave = () => {
    toast.success('Settings saved successfully');
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 bg-gradient-to-br from-slate-50 to-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-[#0052CC] to-indigo-600 rounded-lg premium-shadow">
            <Settings className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl text-gray-900">Settings</h1>
            <p className="text-gray-600 mt-1">Manage your account and integration settings</p>
          </div>
        </div>
      </div>

      {/* Subscription Info Card */}
      <Card className="border-l-4 border-l-emerald-500 premium-shadow hover-lift bg-gradient-to-r from-white to-emerald-50/30">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl blur-md opacity-50"></div>
                <div className="relative w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-lg text-gray-900">Premium Plan</p>
                  <Badge className="bg-gradient-to-r from-emerald-500 to-green-600 border-0">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Active
                  </Badge>
                </div>
                <p className="text-sm text-gray-600">Active until December 31, 2025</p>
              </div>
            </div>
            <Button variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
              Upgrade Plan
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Settings Tabs */}
      <Tabs defaultValue="company" className="w-full">
        <TabsList className="grid w-full grid-cols-4 p-1 bg-white premium-shadow">
          <TabsTrigger value="company" className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-50 data-[state=active]:to-indigo-50 data-[state=active]:text-[#0052CC]">
            <Building2 className="w-4 h-4" />
            Company
          </TabsTrigger>
          <TabsTrigger value="integration" className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-50 data-[state=active]:to-indigo-50 data-[state=active]:text-[#0052CC]">
            <Link className="w-4 h-4" />
            Integration
          </TabsTrigger>
          <TabsTrigger value="api" className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-50 data-[state=active]:to-indigo-50 data-[state=active]:text-[#0052CC]">
            <Key className="w-4 h-4" />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-50 data-[state=active]:to-indigo-50 data-[state=active]:text-[#0052CC]">
            <Bell className="w-4 h-4" />
            Notifications
          </TabsTrigger>
        </TabsList>

        {/* Company Settings */}
        <TabsContent value="company" className="space-y-6 mt-6">
          <Card className="premium-shadow hover-lift">
            <CardHeader className="border-b bg-gradient-to-r from-gray-50 to-slate-50">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#0052CC]" />
                <div>
                  <CardTitle>Company Information</CardTitle>
                  <CardDescription className="mt-1">Update your company details and contact information</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-500" />
                    Company Name
                  </Label>
                  <Input 
                    defaultValue="ABC Trading LLC" 
                    className="h-11 border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-2">
                    <Shield className="w-4 h-4 text-gray-500" />
                    Tax Registration Number (TRN)
                  </Label>
                  <Input 
                    defaultValue="AE123456789" 
                    className="h-11 border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20"
                  />
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-500" />
                    Contact Email
                  </Label>
                  <Input 
                    defaultValue="contact@abctrading.ae" 
                    type="email"
                    className="h-11 border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-500" />
                    Contact Phone
                  </Label>
                  <Input 
                    defaultValue="+971 4 123 4567" 
                    className="h-11 border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20"
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label className="text-sm flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  Business Address
                </Label>
                <Input 
                  defaultValue="Office 123, Business Bay, United Arab Emirates" 
                  className="h-11 border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    City
                  </Label>
                  <Input 
                    defaultValue="UAE" 
                    className="h-11 border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-2">
                    <Globe className="w-4 h-4 text-gray-500" />
                    Country
                  </Label>
                  <Input 
                    defaultValue="United Arab Emirates" 
                    className="h-11 border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleSave} className="bg-gradient-to-r from-[#0052CC] to-indigo-600 hover:from-[#0041A3] hover:to-indigo-700 shadow-lg">
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integration Settings */}
        <TabsContent value="integration" className="space-y-6 mt-6">
          <Card className="premium-shadow hover-lift">
            <CardHeader className="border-b bg-gradient-to-r from-gray-50 to-slate-50">
              <div className="flex items-center gap-2">
                <Link className="w-5 h-5 text-[#0052CC]" />
                <div>
                  <CardTitle>Service Provider Configuration</CardTitle>
                  <CardDescription className="mt-1">Configure your service provider integration endpoints</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Shield className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-900 mb-1">Current Service Provider: <strong>ServicePro UAE</strong></p>
                    <p className="text-xs text-gray-600">All invoice processing is routed through this provider</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Sales Invoice Endpoint URL</Label>
                <Input 
                  defaultValue="https://api.servicepro.ae/v2/invoices/sales" 
                  className="h-11 font-mono text-sm border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20"
                />
                <p className="text-xs text-gray-500">API endpoint for posting sales invoices</p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Purchase Invoice Endpoint URL</Label>
                <Input 
                  defaultValue="https://api.servicepro.ae/v2/invoices/purchase" 
                  className="h-11 font-mono text-sm border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20"
                />
                <p className="text-xs text-gray-500">API endpoint for receiving purchase invoices</p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Webhook URL (for status updates)</Label>
                <Input 
                  defaultValue="https://api.abctrading.ae/webhook/status" 
                  className="h-11 font-mono text-sm border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20"
                />
                <p className="text-xs text-gray-500">Receive real-time status updates from service provider</p>
              </div>

              <Separator />

              <div className="space-y-4">
                <Label className="text-sm">ERP System Selection</Label>
                <div className="grid grid-cols-2 gap-4">
                  {['SAP', 'Oracle', 'Tally', 'Zoho', 'Microsoft Dynamics', 'QuickBooks'].map((erp) => (
                    <div key={erp} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:border-[#0052CC] hover:bg-blue-50 transition-all cursor-pointer">
                      <input type="radio" name="erp" value={erp} defaultChecked={erp === 'SAP'} className="text-[#0052CC]" />
                      <Label className="cursor-pointer">{erp}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleSave} className="bg-gradient-to-r from-[#0052CC] to-indigo-600 hover:from-[#0041A3] hover:to-indigo-700 shadow-lg">
                  <Save className="w-4 h-4 mr-2" />
                  Save Integration Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Keys */}
        <TabsContent value="api" className="space-y-6 mt-6">
          <Card className="premium-shadow hover-lift">
            <CardHeader className="border-b bg-gradient-to-r from-gray-50 to-slate-50">
              <div className="flex items-center gap-2">
                <Key className="w-5 h-5 text-[#0052CC]" />
                <div>
                  <CardTitle>API Keys & Authentication</CardTitle>
                  <CardDescription className="mt-1">Manage your API keys for secure integration</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Shield className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-900 mb-1"><strong>Security Notice</strong></p>
                    <p className="text-xs text-gray-600">Keep your API keys secure. Never share them publicly or commit them to version control.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <p className="text-sm text-gray-900 mb-1">Production API Key</p>
                    <p className="text-xs font-mono text-gray-600">ais_live_*********************abc123</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm">Reveal</Button>
                    <Button variant="outline" size="sm">Regenerate</Button>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <p className="text-sm text-gray-900 mb-1">Test API Key</p>
                    <p className="text-xs font-mono text-gray-600">ais_test_*********************xyz789</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm">Reveal</Button>
                    <Button variant="outline" size="sm">Regenerate</Button>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <Label className="text-sm">Webhook Secret</Label>
                <Input 
                  type="password" 
                  defaultValue="whsec_*********************secret" 
                  className="h-11 font-mono text-sm border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20"
                />
                <p className="text-xs text-gray-500">Used to verify webhook signatures from service provider</p>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleSave} className="bg-gradient-to-r from-[#0052CC] to-indigo-600 hover:from-[#0041A3] hover:to-indigo-700 shadow-lg">
                  <Save className="w-4 h-4 mr-2" />
                  Update API Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications" className="space-y-6 mt-6">
          <Card className="premium-shadow hover-lift">
            <CardHeader className="border-b bg-gradient-to-r from-gray-50 to-slate-50">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#0052CC]" />
                <div>
                  <CardTitle>Notification Preferences</CardTitle>
                  <CardDescription className="mt-1">Configure how you want to receive notifications</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="space-y-0.5">
                    <Label className="text-sm text-gray-900">Invoice Processing Complete</Label>
                    <p className="text-xs text-gray-600">Get notified when an invoice is successfully processed</p>
                  </div>
                  <Switch defaultChecked />
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="space-y-0.5">
                    <Label className="text-sm text-gray-900">Processing Failures</Label>
                    <p className="text-xs text-gray-600">Alert me when an invoice processing fails</p>
                  </div>
                  <Switch defaultChecked />
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="space-y-0.5">
                    <Label className="text-sm text-gray-900">Purchase Invoice Received</Label>
                    <p className="text-xs text-gray-600">Notify when a new purchase invoice requires review</p>
                  </div>
                  <Switch defaultChecked />
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="space-y-0.5">
                    <Label className="text-sm text-gray-900">Weekly Summary Report</Label>
                    <p className="text-xs text-gray-600">Receive a weekly summary of all transactions</p>
                  </div>
                  <Switch />
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="space-y-0.5">
                    <Label className="text-sm text-gray-900">System Maintenance</Label>
                    <p className="text-xs text-gray-600">Updates about scheduled maintenance and downtime</p>
                  </div>
                  <Switch defaultChecked />
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <Label className="text-sm">Notification Email</Label>
                <Input 
                  type="email" 
                  defaultValue="notifications@abctrading.ae" 
                  className="h-11 border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20"
                />
                <p className="text-xs text-gray-500">All notifications will be sent to this email address</p>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleSave} className="bg-gradient-to-r from-[#0052CC] to-indigo-600 hover:from-[#0041A3] hover:to-indigo-700 shadow-lg">
                  <Save className="w-4 h-4 mr-2" />
                  Save Notification Preferences
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
